import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../shared/api/axiosInstance';
import type { PaymentTermsBasis } from '../../../shared/payment-terms';
import type { RevRecBasis } from '../../../generated/api/models/revRecBasis';
import type { VatRegistration } from '../../../generated/api/models/vatRegistration';
// VAT enums single-sourced from shared/config/{vat_filing_frequency,vat_scheme}.config.json
// → apps/frontend/src/generated/config-enums.ts (config_enums_generator.py).
import type { VatFilingFrequency, VatScheme } from '../../../generated/config-enums';
// Card brand / funding value sets single-sourced from shared/config/{card_brand,card_funding}.config.json.
import type { CardBrand, CardFunding } from '../../../generated/config-enums';
// e-Invoicing format value set single-sourced from shared/config/einvoice_format.config.json.
import type { EInvoiceFormat } from '../../../generated/config-enums';

export type { VatRegistration };
export type { VatFilingFrequency, VatScheme };
export type { CardBrand, CardFunding };
export type { EInvoiceFormat };

// OC&CO C2 — one tenant-configured credit-card surcharge rule. A null brand /
// issuer_country / funding is a wildcard (matches any). Stored inside the
// `card_surcharge_rules` JSONB on accounting_tenant_settings; the value set is
// validated at the settings service seam. `surcharge_percent` is persisted as a
// stringified DECIMAL, so reads can be a string — the editor coerces to a number.
export interface CardSurchargeRule {
  brand: CardBrand | null;
  issuer_country: string | null;
  funding: CardFunding | null;
  surcharge_percent: number | string;
  label: string | null;
}

export interface AccountingTenantSettings {
  id: string | null;
  tenant_id: string | null;
  // Invoice numbering
  invoice_format_pattern: string;
  invoice_start_sequence_from: number;
  invoice_seq_reset: string;
  // Payment numbering
  payment_format_pattern: string;
  payment_start_sequence_from: number;
  payment_seq_reset: string;
  // Payment received numbering
  payment_received_format_pattern: string;
  payment_received_start_sequence_from: number;
  payment_received_seq_reset: string;
  // Bill numbering
  bill_format_pattern: string;
  bill_start_sequence_from: number;
  bill_seq_reset: string;
  // T19 — Bank deposit / transfer numbering + per-method routing
  bank_deposit_format_pattern: string;
  bank_deposit_start_sequence_from: number;
  bank_deposit_seq_reset: string;
  bank_transfer_format_pattern: string;
  bank_transfer_start_sequence_from: number;
  bank_transfer_seq_reset: string;
  /** JSONB: { default_clearing_account_id, method_to_bank_account: { ach, wire, card, digital_wallet } } */
  deposit_routing: {
    default_clearing_account_id?: string | null;
    method_to_bank_account?: Record<string, string | null>;
  } | null;
  // Organization branding (company info moved to tenant settings)
  logo_file_id: string | null;
  // Expense-account granularity (bill-import suggestion bias).
  expense_account_detail_level: string;
  // Defaults
  fiscal_year_start_month: number;
  /** Normalized default payment-terms value object (ERP P3). */
  default_payment_terms_id: string | null;
  default_payment_terms_basis: PaymentTermsBasis | null;
  default_payment_terms_days: number | null;
  default_sales_account_id: string | null;
  default_purchase_account_id: string | null;
  default_tax_rate_id: string | null;
  // Income account a sales-return restocking fee posts to (finding [167]).
  restocking_fee_account_id: string | null;
  // Features
  auto_generate_journal_entries: boolean;
  // AP payment-run approval controls (WI a9901d1c). Threshold is nullable —
  // null means "require approval for every run". auto_approve_enabled is the
  // dual-control kill-switch; window_hours drives the anti-structuring guard.
  ap_approval_required_above_amount: number | null;
  ap_auto_approve_enabled: boolean;
  ap_auto_approve_window_hours: number;
  // Israeli tax compliance
  il_osek_morsheh: string | null;
  il_company_registration: string | null;
  il_nikuyim_file_number: string | null;
  il_tax_registration_type: string | null;
  // VAT registration — the tenant's own VAT registration(s) + filing config
  vat_home_country: string | null;
  vat_home_number: string | null;
  vat_registrations: VatRegistration[];
  vat_filing_frequency: VatFilingFrequency | null;
  vat_scheme: VatScheme;
  // e-Invoicing outbound channel (UBL 2.1 / PEPPOL BIS). OFF by default; the
  // seller PEPPOL endpoint has no home outside this settings surface.
  einvoicing_enabled: boolean;
  einvoice_format: EInvoiceFormat;
  einvoice_endpoint_id: string | null;
  einvoice_endpoint_scheme_id: string | null;
  // Procurement defaults
  default_claim_expense_account_id: string | null;
  // Deferred-revenue liability account (feature gap [33]); null = engine off
  deferred_revenue_account_id: string | null;
  // Freight (TMS B5/B6) — persona-gated (sells_freight_services)
  freight_revenue_recognition_basis: RevRecBasis;
  freight_accrued_cost_account_id: string | null;
  freight_accrued_revenue_account_id: string | null;
  freight_accrual_auto_post: boolean;
  // Credit control (G2) — tenant opt-in; default off (no surprise blocking)
  credit_control_enabled: boolean;
  // P2P — AP members appointed to issue outbound payments (Payments Due nags
  // target these users; empty falls back to accounting admins)
  outbound_payment_user_ids: string[];
  // OC&CO C2 — portal self-service payment: card surcharge rules engine + the
  // bank details shown for the wire option.
  card_surcharge_enabled: boolean;
  card_surcharge_credit_only: boolean;
  card_surcharge_rules: CardSurchargeRule[];
  card_surcharge_income_account_id: string | null;
  portal_wire_bank_account_id: string | null;
  portal_wire_instructions: string | null;
  settings: Record<string, unknown>;
  updated_by: string | null;
}

interface TenantSettingsUpdate {
  invoice_format_pattern?: string;
  invoice_start_sequence_from?: number;
  invoice_seq_reset?: string;
  payment_format_pattern?: string;
  payment_start_sequence_from?: number;
  payment_seq_reset?: string;
  payment_received_format_pattern?: string;
  payment_received_start_sequence_from?: number;
  payment_received_seq_reset?: string;
  bill_format_pattern?: string;
  bill_start_sequence_from?: number;
  bill_seq_reset?: string;
  bank_deposit_format_pattern?: string;
  bank_deposit_start_sequence_from?: number;
  bank_deposit_seq_reset?: string;
  bank_transfer_format_pattern?: string;
  bank_transfer_start_sequence_from?: number;
  bank_transfer_seq_reset?: string;
  deposit_routing?: {
    default_clearing_account_id?: string | null;
    method_to_bank_account?: Record<string, string | null>;
  };
  logo_file_id?: string;
  fiscal_year_start_month?: number;
  default_payment_terms_id?: string | null;
  default_payment_terms_basis?: PaymentTermsBasis | null;
  default_payment_terms_days?: number | null;
  default_sales_account_id?: string;
  default_purchase_account_id?: string;
  deferred_revenue_account_id?: string | null;
  default_tax_rate_id?: string;
  restocking_fee_account_id?: string | null;
  auto_generate_journal_entries?: boolean;
  // AP payment-run approval controls (WI a9901d1c). Threshold nullable — null
  // clears it (require approval for every run).
  ap_approval_required_above_amount?: number | null;
  ap_auto_approve_enabled?: boolean;
  ap_auto_approve_window_hours?: number;
  il_osek_morsheh?: string;
  il_company_registration?: string;
  il_nikuyim_file_number?: string;
  il_tax_registration_type?: string;
  // VAT registration (home country/number nullable — null clears to NULL)
  vat_home_country?: string | null;
  vat_home_number?: string | null;
  vat_registrations?: VatRegistration[];
  vat_filing_frequency?: VatFilingFrequency;
  vat_scheme?: VatScheme;
  // e-Invoicing (endpoint id/scheme nullable — null clears to NULL)
  einvoicing_enabled?: boolean;
  einvoice_format?: EInvoiceFormat;
  einvoice_endpoint_id?: string | null;
  einvoice_endpoint_scheme_id?: string | null;
  default_claim_expense_account_id?: string | null;
  // Freight (TMS B5/B6)
  freight_revenue_recognition_basis?: RevRecBasis;
  freight_accrued_cost_account_id?: string | null;
  freight_accrued_revenue_account_id?: string | null;
  freight_accrual_auto_post?: boolean;
  // Credit control (G2)
  credit_control_enabled?: boolean;
  // P2P — designated outbound-payment users
  outbound_payment_user_ids?: string[];
  // OC&CO C2 — portal card surcharge + wire settings (account/bank/instructions
  // nullable — null clears to NULL).
  card_surcharge_enabled?: boolean;
  card_surcharge_credit_only?: boolean;
  card_surcharge_rules?: CardSurchargeRule[];
  card_surcharge_income_account_id?: string | null;
  portal_wire_bank_account_id?: string | null;
  portal_wire_instructions?: string | null;
}

export const accountingSettingsKeys = {
  all: ['accounting-settings'] as const,
  tenant: () => [...accountingSettingsKeys.all, 'tenant'] as const,
};

export function useAccountingTenantSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accountingSettingsKeys.tenant(),
    queryFn: async () => {
      const response = await axiosInstance.get<{ data: AccountingTenantSettings | null }>(
        '/api/v1/accounting/settings/tenant'
      );
      return response.data.data;
    },
    // The tenant-settings read is module-admin only; gate it (e.g. on admin) so a
    // non-admin viewer doesn't fire a guaranteed-403 request on every page.
    enabled: options?.enabled ?? true,
  });
}

export function useUpdateAccountingTenantSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: TenantSettingsUpdate) => {
      const response = await axiosInstance.put<{ data: AccountingTenantSettings }>(
        '/api/v1/accounting/settings/tenant',
        data
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountingSettingsKeys.tenant() });
    },
  });
}
